import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { store } from 'redux-rest-reducer';

import ReactTable from 'react-table';

import { TableWrapper } from 'modules/list/table';
import { Row, Text } from '@cuemath/comps';
import { epochToDate, epochTo24HrTime } from 'utils';
import styled from 'styled-components';
import { Checkbox, Modal, Popup, Button as SemanticButton } from 'semantic-ui-react';
import { getAll as getAllVibes, patch as patchVibe } from 'actions/vibes';
import { Info } from 'styled-icons/octicons/Info';

const CustomInfo = styled(Info)`
  margin-left: 1px;
  &:hover {
    cursor: pointer;
  }
`;

const style = {
  left: '762px',
};

const ACTIONS = {
  INACTIVE: 'mark_inactive',
  ACTIVE: 'mark_active',
};

const DEFAULT = { DATE_FORMAT: 'DD-MM-YYYY' };

const URL = `https://cuemath-intel.s3.ap-southeast-1.amazonaws.com/media/vibes`;

let timeout = null;

class VibeList extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      vibeData: [],
      submitDone: false,
      open: false,
      patchVibeData: store.patch(),
      rowData: null,
      errorText: null,
      message: null,
      staleState: true,
      showConfirmation: false,
      isDisabled: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { vibes, actions } = props;
    const { patchVibeData, staleState } = state;
    const getData = store.getAll(vibes);
    const patchData = store.patch(vibes, patchVibeData.id);
    const { resolved } = patchData || {};
    const newState = {};

    if (getData && getData.isLoaded) {
      newState.vibeData = getData;
    }

    if (resolved && staleState) {
      const { isPatched, isPatchingFailed } = resolved || {};
      if (isPatched) {
        newState.submitDone = true;
        newState.errorText = '';
        newState.message = 'Successfully updated!';
        actions.getAllVibes();
      }
      if (isPatchingFailed) {
        newState.submitDone = true;
        newState.message = '';
        newState.errorText = 'Error encountered! ';
      }
      newState.staleState = false;
    }

    return newState;
  }

  static defaultProps = {
    activeTab: null,
  };

  static propTypes = {
    activeTab: PropTypes.string,
  };

  componentDidUpdate(prevProps, prevState) {
    const { submitDone } = this.state;
    if (!prevState.submitDone && submitDone) {
      timeout = setTimeout(() => {
        this.handleCancel();
      }, 1500);
    }
  }

  componentWillUnmount() {
    clearTimeout(timeout);
  }

  componentDidMount() {
    this.refresh();
  }

  refresh = () => {
    const { actions } = this.props;
    actions.getAllVibes();
  };

  handleIsActive = vibe => {
    this.setState({ open: true, rowData: vibe, showConfirmation: true });
  };

  onConfirm = () => {
    const {
      rowData,
      patchVibeData: { id: patchId },
    } = this.state;

    const { actions } = this.props;

    const { is_active, id: vibeId } = rowData || {};

    const patchObj = is_active
      ? { action: ACTIONS.INACTIVE }
      : { action: ACTIONS.ACTIVE };

    actions.patchVibe(patchId, vibeId, patchObj);
    this.setState({ staleState: true, isDisabled: true });
  };

  handleCancel = () => {
    this.setState({
      open: false,
      showConfirmation: false,
      errorText: null,
      message: null,
      submitDone: false,
      isDisabled: false,
    });
  };

  convertDateToformat = epochtime => {
    const date = epochToDate(epochtime, DEFAULT.DATE_FORMAT);
    const time = epochTo24HrTime(epochtime);
    const format = `${date} ${time}`;
    return format;
  };

  render() {
    const {
      vibeData,
      open,
      showConfirmation,
      message,
      errorText,
      isDisabled,
    } = this.state;
    const { data: vibeDataList } = vibeData || {};
    const { data: dataArr = [] } = vibeDataList || {};
    const tableColumns = [
      {
        Header: `User Vibes`,
        columns: [
          {
            Header: 'Created On',
            accessor: 'created_on',
            width: 150,
            Cell: ({ value }) => this.convertDateToformat(value),
          },
          {
            Header: 'Vibe Name',
            accessor: 'vibe_name',
            width: 230,
          },
          {
            Header: 'User Type',
            accessor: 'user_type',
            width: 80,
          },
          {
            Header: 'Start Date',
            accessor: 'start_date',
            width: 100,
            Cell: ({ value }) => epochToDate(value, DEFAULT.DATE_FORMAT),
          },
          {
            Header: 'End Date',
            accessor: 'end_date',
            width: 100,
            Cell: ({ value }) => epochToDate(value, DEFAULT.DATE_FORMAT),
          },
          {
            Header: 'Status',
            accessor: 'status',
            width: 150,
            Cell: ({ row }) => {
              const {
                _original: { status },
              } = row || {};
              return (
                <Row justifyContent="center">
                  <Text>{status}</Text>
                  {status === 'ERROR' && (
                    <Popup
                      content="Formatting Error"
                      trigger={<CustomInfo size={14} />}
                      style={style}
                    />
                  )}
                </Row>
              );
            },
          },
          {
            Header: 'Is Active',
            accessor: 'is_active',
            width: 70,
            Cell: ({ row }) => {
              const { _original } = row || {};
              const { status, is_active: isActive } = _original || {};
              const disabled = ['ERROR', 'PROCESSING', 'STOPPED', 'COMPLETED'].includes(
                status,
              );
              return (
                <Checkbox
                  name="is_active"
                  toggle
                  label=""
                  onChange={() => this.handleIsActive(_original)}
                  disabled={disabled}
                  checked={isActive}
                />
              );
            },
          },
          {
            Header: 'CSV Link',
            accessor: 'users_file_name',
            width: 70,
            Cell: ({ row }) => {
              const { _original } = row || {};
              const { users_file_name: usersFileName } = _original || {};
              const fileName = usersFileName.split('_')[0];
              return (
                <a href={`${URL}/${fileName}.csv`} download>
                  Link
                </a>
              );
            },
          },
          {
            Header: 'Embed Code',
            accessor: 'embed_code',
            width: 120,
          },
          {
            Header: 'Vibe id',
            accessor: 'id',
            width: 320,
          },
        ],
      },
    ];

    const sort = [{ id: 'created_on', desc: true }];
    const confirmationMessage = errorText || message;

    return (
      <>
        <TableWrapper>
          <ReactTable
            data={dataArr}
            className="-striped -highlight"
            showPagination
            showPageSizeOptions
            filterable={false}
            resizable={false}
            defaultSorted={sort}
            defaultPageSize={20}
            columns={tableColumns}
            style={{ textAlign: 'center' }}
          />
        </TableWrapper>
        {showConfirmation && !errorText && !message && (
          <Modal open={open} size="tiny">
            <Modal.Header>Change IsActive state</Modal.Header>
            <Modal.Content>
              <p>Are you sure you want to make these changes?</p>
            </Modal.Content>
            <Modal.Actions>
              <SemanticButton
                disabled={isDisabled}
                loading={isDisabled}
                positive
                onClick={this.onConfirm}
              >
                Yes
              </SemanticButton>
              <SemanticButton
                negative
                disabled={isDisabled}
                onClick={() => this.setState({ open: false, rowData: null })}
              >
                No
              </SemanticButton>
            </Modal.Actions>
          </Modal>
        )}
        {confirmationMessage && (
          <Modal onClose={this.handleCancel} open={open} size="tiny">
            <Modal.Header>{confirmationMessage}</Modal.Header>
          </Modal>
        )}
      </>
    );
  }
}

export default connect(
  ({ vibes }) => ({
    vibes,
  }),
  dispatch => ({
    actions: bindActionCreators({ getAllVibes, patchVibe }, dispatch),
  }),
)(VibeList);
